import { Injectable, Inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { first } from "rxjs/operators";

import { LocalStorage } from "#services/local-storage.service";
import { Cache } from "#models/cache.interface";
import { LocalStorageCache } from "#services/local-storage-cache.service";
import {PPUser as User} from "cli-types";
import { School } from "@carnegie-learning/data-access-fido";
@Injectable()
export class LoggerService {
    private static readonly FLUSH_SIZE: number = (<any>window).loggerFlushSize;
    LOGGER_PATH = (<any>window).loggerCLAPI;

    public type = "ServerLogService";
    private cache: Cache;
    private sessionId = "";
    private userId = "";
    private semaphore = false;
    private tokenExists = false;

    constructor(private http: HttpClient, private localStorage: LocalStorage) {
        this.cache = new LocalStorageCache("passport-logging");

        if ((<any>window).env !== "local") {
            this.send();
            setInterval(() => {
                this.send();
            }, 10000);
        }
    }

    /**
     * @description
     * Send logging messages either from local storage cache when not in local env
     */
    public send(): void {
        const cacheLength = this.cache.getIndex();

        if (cacheLength && cacheLength > 0 && !this.semaphore) {
            this.semaphore = true; // used to prevent multiple requests

            const token = this.localStorage.get("authToken");
            this.sessionId = token ? token : "null"; // we're using the token as the sessionID since there are no other unique identifiers
            const httpOptions = {
                headers: new HttpHeaders({ "X-CTP-Session-Id": this.sessionId })
            };

            this.http
                .post(
                    this.LOGGER_PATH,
                    this.cache.getItems(cacheLength),
                    httpOptions
                )
                .pipe(first())
                .subscribe(
                    response => {
                        this.cache.removeAndSort(cacheLength, () => {
                            this.semaphore = false;
                        });
                    },
                    error => {
                        this.semaphore = false;
                    }
                );
        }
    }

    /**
     * @description
     * Store logging messages in local storage
     *
     * @param {string} eventDetails The logging event in string format
     * @param {object} options Optional data to store in our log.
     */
    public cacheMessage(eventDetails: object, options?: object): void {
        if ((<any>window).env === "local") {
            return; // blank return to prevent logging during
        }

        // Define additional params - PV2-2530
        let role = "";
        let school: string | null = null;
        let program = null;

        const userProfile: User = this.localStorage.get("userProfile");
        const selectedCourse = this.localStorage.get("selectedCourse");
        if (userProfile && userProfile.userID) {
            this.userId = userProfile.userGUID;
            switch (userProfile.roleLevel) {
                case 1:
                    role = "admin";
                    break;
                case 2:
                    role = "admin";
                    break;
                case 3:
                    role = "admin";
                    break;
                case 4:
                    role = "teacher";
                    const schoolData = this.localStorage.get("fidoSchoolList");
                    school = "Need to fix this"; // If this passes through then schools are missing
                    if (userProfile.schools && userProfile.schools.length > 0 && schoolData && schoolData.length > 0) {
                        school = schoolData[0].name;
                    }
                    if (selectedCourse && selectedCourse.languageName) {
                        program = selectedCourse.languageName;
                    }
                    break;
                case 5:
                    role = "student";

                    if (selectedCourse && selectedCourse.languageName) {
                        program = selectedCourse.languageName;

                        const studentSchoolData = this.localStorage.get("fidoSchoolList");
                        school = "Need to fix this"; // If this passes through then schools are missing
                        if (selectedCourse.schoolID && studentSchoolData && studentSchoolData.length > 0) {
                            studentSchoolData.forEach((data: School) => {
                                if (selectedCourse.schoolID === data.schoolid) {
                                    school = data.name;
                                }
                            });
                        }
                    }
                    break;
                default:
                    break;
            }
        } else {
            this.userId = "ffffffff-ffff-ffff-ffff-ffffffffffff";
        }

        const token = this.localStorage.get("authToken");
        this.sessionId = token ? token : null; // we're using the token as the sessionID since there are no other unique identifiers
        this.tokenExists = !!token;

        const message: object = {
            product_id: (<any>window).env + "_passport_v2",
            version: (<any>window).buildNumber,
            clientTime: Date.now().toString(),
            tokenExists: this.tokenExists,
            originatingSessionId: this.sessionId,
            uuid: this.userId
        };

        // Modify event details to include extra details
        Object.assign(eventDetails, {
            role: role,
            school: school,
            program: program
        });

        // Add event details to the our message object
        Object.assign(message, eventDetails);

        // If options is defined, add those to our object.
        if (options) {
            Object.assign(message, options);
        }

        this.cache.setItem(this.toParams(message));

        // Send the cache if we reached our maximum flush size
        if (this.cache.getIndex() >= LoggerService.FLUSH_SIZE) {
            this.send();
        }
    }

    /**
     * @description
     * Used to create a string of key/value parameters from an object
     *
     * @param {object} obj The object that we are using to create our string
     * @return {string} the object converted to a string
     */
    private toParams(obj: {[index:string]:any}): string {
        let str = "";

        // loop the properties of obj and create a comma separated list.
        for (const prop in obj) {
            if (Array.isArray(obj[prop])) {
                if (obj[prop].length > 0) {
                    str =
                        str +
                        prop +
                        "=" +
                        obj[prop].toString().replace(/,/g, " ") +
                        ",";
                }
            } else {
                str = str + prop + "=" + obj[prop] + ",";
            }
        }

        // Make sure to trim off any remaining comma added by the for in loop
        const re = new RegExp("[,]+$");
        str = str.replace(re, "");

        return str;
    }
}
